import { ConsoleLogger } from "@microsoft/signalr/dist/esm/Utils";
import { settings } from "../Settings";
import { startRCCallRecording, stopRCCallRecording } from "../api/apiManager";
import { setConnectedRCCall, setRCCallEnded } from "../store/slice/CallSlice";
import { getStore } from "../store/storeRegistry";

const normalizeDialString = (value) => {
  if (!value) return "";
  return String(value)
    .trim()
    .replace(/[^\d*#+]/g, "")
    .replace(/^\+/, "");
};

export const CALL_STATUS = {
  IDLE: "Idle",
  CALLING: "Calling...",
  TRYING: "Trying...",
  RINGING: "Ringing...",
  CONNECTED: "Connected",
  IVR: "IVR Menu",
  HOLD: "On Hold",
  DISCONNECTED: "Call Ended",
  FAILED: "Failed",
  BUSY: "Busy",
  NO_ANSWER: "No Answer",
};
const store = {
  dispatch: (...args) => getStore().dispatch(...args),
  getState: (...args) => getStore().getState(...args),
};
class RingCentralWebPhoneService {
  constructor() {
    this.webPhone = null;
    this.currentSession = null;
    this.isReady = false;
    this.isConnecting = false;
    this.lastStatus = CALL_STATUS.IDLE;
    this.lastError = "";
    this.listeners = new Set();
    this._silenceInterval = null;
    this._stateInterval = null;
    this._disposeInterval = null;
    this._connectedTimer = null;
  }

  // =====================
  // STATE
  // =====================
  getState() {
    return {
      isReady: this.isReady,
      isConnecting: this.isConnecting,
      lastError: this.lastError,
      currentSession: this.currentSession,
      status: this.lastStatus,
    };
  }

  getCurrentSession() {
    return this.currentSession;
  }

  _getNestedValue(source, path) {
    if (!source || !path) return undefined;
    return path.split(".").reduce((value, key) => {
      if (value == null) return undefined;
      return value[key];
    }, source);
  }

  getRecordingContext() {
    const session = this.currentSession || window?.__rcSession || null;
    const telephonySessionId = [
      "telephonySessionId",
      "sessionId",
      "id",
      "callId",
      "callSessionId",
      "data.telephonySessionId",
      "data.sessionId",
      "call.telephonySessionId",
      "call.sessionId",
      "_session.telephonySessionId",
      "_session.sessionId",
    ]
      .map((path) => this._getNestedValue(session, path))
      .find(Boolean);

    const partyId = [
      "partyId",
      "localPartyId",
      "participantId",
      "data.partyId",
      "call.partyId",
      "call.localPartyId",
      "_session.partyId",
      "_session.localPartyId",
    ]
      .map((path) => this._getNestedValue(session, path))
      .find(Boolean);

    const recordingId = [
      "recordingId",
      "data.recordingId",
      "call.recordingId",
      "_session.recordingId",
    ]
      .map((path) => this._getNestedValue(session, path))
      .find(Boolean);

    return {
      session,
      telephonySessionId,
      partyId,
      recordingId,
    };
  }

  async startRecording(userId) {
    const recordingContext = this.getRecordingContext();
    const telephonySessionId = recordingContext?.telephonySessionId;
    const partyId = recordingContext?.partyId;

    if (!telephonySessionId || !partyId) {
      throw new Error("RingCentral recording context not available");
    }

    const response = await startRCCallRecording(
      userId,
      telephonySessionId,
      partyId,
    );

    const recordingId =
      response?.data?.result?.recordingId ??
      response?.data?.recordingId ??
      response?.data?.result?.id ??
      response?.data?.id ??
      response?.recordingId ??
      recordingContext?.recordingId ??
      null;

    return {
      response,
      telephonySessionId,
      partyId,
      recordingId,
    };
  }

  async stopRecording(userId, recordingId) {
    const recordingContext = this.getRecordingContext();
    const telephonySessionId = recordingContext?.telephonySessionId;
    const partyId = recordingContext?.partyId;

    if (!telephonySessionId || !partyId) {
      throw new Error("RingCentral recording context not available");
    }

    const resolvedRecordingId = recordingId || recordingContext?.recordingId;
    const response = await stopRCCallRecording(
      userId,
      telephonySessionId,
      partyId,
      resolvedRecordingId,
    );

    return {
      response,
      telephonySessionId,
      partyId,
      recordingId: resolvedRecordingId,
    };
  }

  // =====================
  // EVENTS
  // =====================
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(payload) {
    this.listeners.forEach((fn) => fn(payload));
  }

  setStatus(status) {
    if (this.lastStatus === status) return;
    this.lastStatus = status;
    this.emit({ type: "status", status });
  }

  // =====================
  // SDK
  // =====================
  getSdk() {
    const SDK = window?.WebPhone;
    if (!SDK) throw new Error("WebPhone SDK not found");
    return SDK;
  }

  // =====================
  // AUDIO
  // =====================
  _startSilenceInterval() {
    this._stopSilenceInterval();
    this._silenceInterval = setInterval(() => {
      document.querySelectorAll("audio").forEach((audio) => {
        if (!audio.paused && audio.src) {
          audio.muted = true;
          audio.volume = 0;
        }
      });
    }, 1000);
  }

  _stopSilenceInterval() {
    if (this._silenceInterval) {
      clearInterval(this._silenceInterval);
      this._silenceInterval = null;
    }
  }

  // =====================
  // INIT
  // =====================
  async init(data = {}) {
    try {
      this.isConnecting = true;
      const SDK = this.getSdk();
      const sipInfo = data?.sipInfo;
      if (!sipInfo) throw new Error("SIP info missing");

      this.webPhone = new SDK({
        sipInfo,
        clientId: data?.clientId,
        debug: false,
        audioHelper: {
          enabled: true,
          incoming: null,
          outgoing: null,
        },
        enableQos: false,
        enableMusicOnHold: false,
      });

      await this.webPhone.start();

      this.isReady = true;
      this.setStatus(CALL_STATUS.IDLE);

      return true;
    } catch (err) {
      this.lastError = err?.message;
      this.isReady = false;
      throw err;
    } finally {
      this.isConnecting = false;
    }
  }

  // =====================
  // CALL
  // =====================

  async call(to, callerId) {
    if (!this.isReady) throw new Error("WebPhone not ready");

    const number = normalizeDialString(to);
    this.setStatus(CALL_STATUS.CALLING);
    // this._startSilenceInterval();

    this.webPhone.on("outboundCall", (session) => {
      this.currentSession = session;
      window.__rcSession = session;
      this._pollSessionState(session);
    });

    // sipClient messages monitor karo - reject detect karne ke liye
    const sipClient = this.webPhone?.sipClient;
    if (sipClient) {
      const handleInbound = (message) => {
        if (
          message?.subject?.includes("487") ||
          message?.subject?.includes("486") ||
          message?.subject?.includes("603") ||
          message?.subject?.includes("480")
        ) {
          this._stopAllTimers();
          this._stopSilenceInterval();
          this.currentSession = null;
          sipClient.off("inboundMessage", handleInbound);
          this.setStatus(CALL_STATUS.DISCONNECTED);
        }
      };
      sipClient.on("inboundMessage", handleInbound);
    }

    const session = await this.webPhone.call(number, callerId);
    return session;
  }

  // =====================
  // region SESSION STATE-
  // =====================

  _pollSessionState(session) {
    if (!session) return;
    this._stopAllTimers();
    this.setStatus(CALL_STATUS.CALLING);
    session.on("ringing", () => {
      this.setStatus(CALL_STATUS.TRYING);
    });
    session.on("answered", () => {
      this.setStatus(CALL_STATUS.RINGING);
    });

    const handleEnd = () => {
      if (!this.currentSession) return;
      this.currentSession = null;
      this.setStatus(CALL_STATUS.DISCONNECTED);
      store.dispatch(setRCCallEnded(true));
      store.dispatch(setConnectedRCCall(false));
    };

    session.on("disposed", () => {
      handleEnd();
    });
    session.on("terminated", () => {
      handleEnd();
    });
    session.on("failed", () => {
      handleEnd();
    });
    session.on("bye", () => {
      handleEnd();
    });
    session.on("ended", () => {
      handleEnd();
    });
    session.on("cancel", () => {
      handleEnd();
    });
    session.on("rejected", () => {
      handleEnd();
    });
    //  Dispose check
    // const disposeCheck = setInterval(() => {
    //   // alert("Checking call status...");
    //   if (session?.state === "disposed" || session?.state === "failed") {
    //     clearInterval(disposeCheck);
    //     this._disposeInterval = null;
    //     this._stopAllTimers();
    //     this.currentSession = null;
    //     this._stopSilenceInterval();
    //     this.setStatus(CALL_STATUS.DISCONNECTED);
    //     store.dispatch(setRCCallEnded(true));
    //     store.dispatch(setConnectedRCCall(false));
    //   }
    // }, 300);

    // this._disposeInterval = disposeCheck;
  }

  _setupRemoteAudio(session) {
    const trySetup = () => {
      const pc = session?.rtcPeerConnection; // ✅ correct property
      const audioEl = session?.audioElement; // ✅ RC ka audio element
      const mediaStream = session?._mediaStream; // ✅ remote stream

      if (!pc && !audioEl) {
        setTimeout(trySetup, 300);
        return;
      }

      // RC ka audioElement already play kar raha hai remote audio
      if (audioEl) {
        this.remoteStream = audioEl.srcObject || mediaStream;
        console.log("✅ Remote stream set from audioElement");
      }

      if (pc) {
        pc.ontrack = (event) => {
          console.log("🎵 Remote track received:", event.streams);
          if (event.streams?.[0]) {
            this.remoteStream = event.streams[0];
          }
        };

        pc.oniceconnectionstatechange = () => {
          console.log("🧊 ICE State:", pc.iceConnectionState);
        };
      }
    };

    setTimeout(trySetup, 200);
  }

  // =====================
  // STOP ALL TIMERS
  // =====================
  _stopAllTimers() {
    if (this._stateInterval) {
      clearInterval(this._stateInterval);
      this._stateInterval = null;
    }
    if (this._disposeInterval) {
      clearInterval(this._disposeInterval);
      this._disposeInterval = null;
    }
    if (this._connectedTimer) {
      clearTimeout(this._connectedTimer);
      this._connectedTimer = null;
    }
  }

  // =====================
  // DTMF
  // =====================
  sendDTMF(digit) {
    try {
      if (!this.currentSession) {
        console.warn("[DTMF] No session!");
        return;
      }
      if (typeof this.currentSession.sendDtmf === "function") {
        this.currentSession.sendDtmf(digit);
        console.log("[DTMF] sent:", digit);
      } else {
        console.error("[DTMF] sendDtmf not found!");
      }
    } catch (e) {
      console.error("[DTMF] error:", e);
    }
  }

  async sendDTMFSequence(sequence, delay = 600) {
    if (!sequence) return;
    for (let i = 0; i < sequence.length; i++) {
      this.sendDTMF(sequence[i]);
      await new Promise((r) => setTimeout(r, delay));
    }
  }

  // =====================
  // ACTIONS
  // =====================
  async hangup() {
    this._stopAllTimers();
    this._stopSilenceInterval();

    if (!this.currentSession) {
      // Session nahi hai but dispatch karo taake UI clean ho
      store.dispatch(setRCCallEnded(true));
      store.dispatch(setConnectedRCCall(false));
      return;
    }

    try {
      const session = this.currentSession;
      this.currentSession = null; // pehle null karo

      //  Multiple ways try karo session terminate karne ke
      if (typeof session.hangup === "function") {
        await session.hangup();
      } else if (typeof session.terminate === "function") {
        await session.terminate();
      } else if (typeof session.dispose === "function") {
        await session.dispose();
      } else if (typeof session.bye === "function") {
        await session.bye();
      }
      console.log("RC Session terminated");
    } catch (err) {
      console.error("RC hangup error:", err);
    }
    this.setStatus(CALL_STATUS.DISCONNECTED);
    store.dispatch(setRCCallEnded(true));
    store.dispatch(setConnectedRCCall(false));
  }

  async mute(muted) {
    if (!this.currentSession) return;
    try {
      muted ? this.currentSession.mute?.() : this.currentSession.unmute?.();
    } catch {}
  }

  async hold(held) {
    if (!this.currentSession) return;
    try {
      if (held) {
        await this.currentSession.hold?.();
        this.setStatus(CALL_STATUS.HOLD);
      } else {
        await this.currentSession.unhold?.();
        this.setStatus(CALL_STATUS.CONNECTED);
      }
    } catch {}
  }
}

const ringCentralWebPhoneService = new RingCentralWebPhoneService();
window.__rcService = ringCentralWebPhoneService;
export default ringCentralWebPhoneService;
